import { useBuildMarketplaceAddOns } from './add-ons/use-build-addons';
import { AddOns } from '../constants/add-ons';
import { useEnableRateShopperFreemium } from '../feature-flags/hooks/use-enable-rate-shopper-freemium';
import { useAutomatedRateShopperFeatureGate } from '../feature-gates/use-automated-rate-shopper-feature-gate';
import { useFreemiumRateShopperFeatureGate } from '../feature-gates/use-freemium-rate-shopper-feature-gate';
import { FeatureAccessValue } from '../feature-gates/use-plan-feature-gate';

/** @type {{ readonly FREEMIUM: 'FREEMIUM', readonly PREMIUM: 'PREMIUM' }} */
export const ACCESS_LEVELS = {
  FREEMIUM: 'FREEMIUM',
  PREMIUM: 'PREMIUM',
};

/**
 * @deprecated
 * RateShopper was originally released under the "Add-Ons" framework and thus users would have to purchase the Add-On for the feature.
 * Later the concept of "Freemium" RateShopper was conceived wherein *some* RateShoppers would be available without purchase but the full feature would still require an add-on purchase.
 * This approach was to inspire users to purchase the Add-On. Now that we're converting this into a subscription-plan-gated feature,
 * We can consider this older approach as the legacy pattern. It's extracted here into a hook that provides a default access level for use
 * before we migrate users into the "new lineup" subscription-plan-gated RateShopper paradigm.
 * @returns {import('../feature-gates/use-plan-feature-gate').FeatureAccessValueType}
 */
const useGetBackwardsCompatibleFreemiumRateShopperAccess = () => {
  // ** source of truth, coupling Premium & Freemium
  // defensive check to make sure only users who can buy Premium, get access to Freemium
  const { addOns } = useBuildMarketplaceAddOns();
  const isPremiumRateShopperAvailableForPurchase = addOns.some((addOn) => addOn.code === AddOns.ShippingStrategies);
  const hasFreemiumAccess = isPremiumRateShopperAvailableForPurchase;
  return hasFreemiumAccess ? FeatureAccessValue.Enabled : FeatureAccessValue.Disabled;
};

/**
 * Used to gate Rate Shopper related features based on the user's access level.
 * @param {'FREEMIUM' | 'PREMIUM'} requestedAccessLevel
 * @returns {boolean} whether the user's access level is sufficent
 */
export const useRequiresRateShopperAccessLevel = (requestedAccessLevel) => {
  const { isFeatureEnabled: hasPremiumAccess } = useAutomatedRateShopperFeatureGate();

  // ** release lever to be deleted once user targeting can be delegated to the feature gates
  const isRateShopperFreemiumEnabled = useEnableRateShopperFreemium();

  const backwardsCompatibleFreemiumAccess = useGetBackwardsCompatibleFreemiumRateShopperAccess();
  const { isFeatureEnabled: hasFreemiumRateShopper } = useFreemiumRateShopperFeatureGate(backwardsCompatibleFreemiumAccess);

  /**
   * @todo resolve this feature flag. It's a separate lever from the seller feature gates.
   * We need a shared strategy on how best to de-/couple the ff + sfg. (May 2025)
   * When flag is off, leverage previous access logic: "has all or has nothing"
   */
  if (!isRateShopperFreemiumEnabled) {
    return hasPremiumAccess;
  }

  if (requestedAccessLevel === ACCESS_LEVELS.FREEMIUM) {
    return hasFreemiumRateShopper;
  }

  if (requestedAccessLevel === ACCESS_LEVELS.PREMIUM) {
    return hasPremiumAccess;
  }

  return hasPremiumAccess;
};
